import type { LessonTemplateNotation } from "./lessonTemplates";
import { ann, circle, line } from "./lessonTemplates";

export type PracticeFigureStep = {
  caption: { en: string; he: string };
  notations: LessonTemplateNotation[];
};

function flattenSteps(
  steps: readonly PracticeFigureStep[],
): LessonTemplateNotation[] {
  return steps.flatMap((step) => step.notations);
}

function rect(
  c1: number,
  r1: number,
  c2: number,
  r2: number,
): LessonTemplateNotation[] {
  return [
    line(c1, r1, c2, r1),
    line(c1, r2, c2, r2),
    line(c1, r1, c1, r2),
    line(c2, r1, c2, r2),
  ];
}

function hatch(
  c1: number,
  r1: number,
  c2: number,
  r2: number,
): LessonTemplateNotation[] {
  return [line(c1, r1, c2, r2), line(c2, r1, c1, r2)];
}

function fractionBar(
  left: number,
  top: number,
  den: number,
  shaded: number,
  label: string,
): LessonTemplateNotation[] {
  const partW = 3;
  const height = 2;
  const right = left + den * partW;
  const bottom = top + height;
  const out = rect(left, top, right, bottom);
  for (let i = 1; i < den; i += 1) {
    const x = left + i * partW;
    out.push(line(x, top, x, bottom));
  }
  const fill = Math.max(0, Math.min(shaded, den));
  for (let i = 0; i < fill; i += 1) {
    out.push(...hatch(left + i * partW, top, left + (i + 1) * partW, bottom));
  }
  out.push(ann(right + 0.6, top + 1, label));
  return out;
}

export function fractionAddBarsSteps(
  n1: number,
  n2: number,
  den: number,
): PracticeFigureStep[] {
  return [
    {
      caption: {
        en: `Shade ${n1}/${den}`,
        he: `צובעים ${n1}/${den}`,
      },
      notations: fractionBar(8, 10, den, n1, `${n1}/${den}`),
    },
    {
      caption: {
        en: `Shade ${n2}/${den}`,
        he: `צובעים ${n2}/${den}`,
      },
      notations: fractionBar(8, 14, den, n2, `${n2}/${den}`),
    },
    {
      caption: {
        en: "Add the shaded parts. Keep the denominator.",
        he: "מחברים את החלקים הצבועים. משאירים את המכנה.",
      },
      notations: [],
    },
  ];
}

/** Two bars, each split into the shared denominator, with the addends shaded. */
export function fractionAddBarsFigure(
  n1: number,
  n2: number,
  den: number,
): LessonTemplateNotation[] {
  return flattenSteps(fractionAddBarsSteps(n1, n2, den));
}

export function percentBarSteps(
  parts: 2 | 4 | 10,
  whole: number,
  percent: 10 | 25 | 50,
): PracticeFigureStep[] {
  const left = 8;
  const top = 10;
  const partW = parts === 10 ? 2 : 4;
  const height = 2;
  const right = left + parts * partW;
  const bottom = top + height;
  const outline = rect(left, top, right, bottom);
  for (let i = 1; i < parts; i += 1) {
    const x = left + i * partW;
    outline.push(line(x, top, x, bottom));
  }
  outline.push(ann(left + parts * partW * 0.5, top - 1.2, String(whole)));
  return [
    {
      caption: {
        en: `Split ${whole} into ${parts} equal parts`,
        he: `מחלקים את ${whole} ל-${parts} חלקים שווים`,
      },
      notations: outline,
    },
    {
      caption: {
        en: `Shade one part — that is ${percent}%`,
        he: `צובעים חלק אחד — זה ${percent}%`,
      },
      notations: [
        ...hatch(left, top, left + partW, bottom),
        ann(left + 0.3, top + 1, `${percent}%`),
      ],
    },
  ];
}

/** A bar split into 2, 4, or 10 equal parts, with one part shaded. */
export function percentBarFigure(
  parts: 2 | 4 | 10,
  whole: number,
  percent: 10 | 25 | 50,
): LessonTemplateNotation[] {
  return flattenSteps(percentBarSteps(parts, whole, percent));
}

export function decimalNumberLineSteps(
  tenths: readonly number[],
): PracticeFigureStep[] {
  const left = 8;
  const row = 12;
  const width = 20;
  const axis: LessonTemplateNotation[] = [
    line(left, row, left + width, row, { arrowRight: true }),
  ];
  for (let t = 0; t <= 10; t += 1) {
    const col = left + (t / 10) * width;
    axis.push(line(col, row - 0.4, col, row + 0.4));
  }
  axis.push(ann(left, row + 1.3, "0"), ann(left + width, row + 1.3, "1"));
  const steps: PracticeFigureStep[] = [
    {
      caption: {
        en: "Number line from 0 to 1",
        he: "ישר המספרים מ-0 עד 1",
      },
      notations: axis,
    },
  ];
  const seen = new Set<number>();
  for (const value of tenths) {
    if (value <= 0 || value >= 10 || seen.has(value)) continue;
    seen.add(value);
    const col = left + (value / 10) * width;
    steps.push({
      caption: {
        en: `Mark 0.${value}`,
        he: `מסמנים 0.${value}`,
      },
      notations: [
        line(col, row - 1.3, col, row),
        ann(col, row - 1.7, `0.${value}`),
      ],
    });
  }
  return steps;
}

/** Number line from 0 to 1 with the given tenths marked. */
export function decimalNumberLineFigure(
  tenths: readonly number[],
): LessonTemplateNotation[] {
  return flattenSteps(decimalNumberLineSteps(tenths));
}

export function dataDotPlotSteps(
  values: readonly number[],
): PracticeFigureStep[] {
  if (!values.length) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 1);
  const left = 8;
  const width = 24;
  const axisRow = 20;
  const counts = new Map<number, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  const steps: PracticeFigureStep[] = [
    {
      caption: {
        en: `Number line from ${min} to ${max}`,
        he: `ישר המספרים מ-${min} עד ${max}`,
      },
      notations: [
        line(left, axisRow, left + width, axisRow, { arrowRight: true }),
        ann(left, axisRow + 1.2, String(min)),
        ann(left + width, axisRow + 1.2, String(max)),
      ],
    },
  ];
  const unique = [...counts.keys()].sort((a, b) => a - b);
  for (const value of unique) {
    const count = counts.get(value) ?? 0;
    const col = left + ((value - min) / span) * width;
    const notations: LessonTemplateNotation[] = [
      line(col, axisRow - 0.35, col, axisRow + 0.35),
    ];
    for (let i = 0; i < count; i += 1) {
      notations.push(circle(col, axisRow - 1.3 - i * 1.15, 0.35));
    }
    steps.push({
      caption: {
        en: `Place ${count} dot${count === 1 ? "" : "s"} at ${value}`,
        he: `שמים ${count} נקודות ב-${value}`,
      },
      notations,
    });
  }
  steps.push({
    caption: {
      en: "What typical value sits in the middle of this pile?",
      he: "איזה ערך טיפוסי יושב באמצע הערימה הזו?",
    },
    notations: [],
  });
  return steps;
}

/** Number line with a stacked dot for each data value. */
export function dataDotPlotFigure(
  values: readonly number[],
): LessonTemplateNotation[] {
  return flattenSteps(dataDotPlotSteps(values));
}

export function flattenFigureSteps(
  steps: readonly PracticeFigureStep[],
): LessonTemplateNotation[] {
  return flattenSteps(steps);
}

/** Marks included when playing through `through` (inclusive), skipping hidden steps. */
export function notationsThroughFigureStep(
  steps: readonly PracticeFigureStep[],
  through: number,
  hidden: ReadonlySet<number> = new Set(),
): LessonTemplateNotation[] {
  const out: LessonTemplateNotation[] = [];
  steps.forEach((step, index) => {
    if (index > through || hidden.has(index)) return;
    out.push(...step.notations);
  });
  return out;
}
